/**
 * "Nearby NPCs" for the Quests panel (sprint-assembly-1, AD-647).
 *
 * The join ack carries EVERY NPC in the zone (32 in Driftwell, 15 in the Reach), and the
 * panel used to list them all, alphabetically, above the dialogue. A player who pressed
 * "talk" saw nothing change: the dialogue opened below a screen of names, and an NPC 80 m
 * away refuses to talk anyway (the server's 6 m interact range). This keeps the list to the
 * NPCs actually near the player, nearest first, and says how many are further off.
 *
 * Display only: the server still decides who is in range (server authority).
 */

/** How far away an NPC still counts as "nearby" for the list (m). */
export const NEARBY_NPC_RADIUS_M = 20;

export interface NpcAt {
  id: string;
  x: number;
  z: number;
}

export interface NearbyNpcs<T extends NpcAt> {
  /** NPCs within the radius, nearest first, each with its distance (m). */
  near: { npc: T; distanceM: number }[];
  /** How many NPCs in the zone are further away than the radius. */
  further: number;
}

/**
 * The NPCs within `radiusM` of `self`, nearest first (ties by id, so the order is stable).
 * With no known own position (not yet joined, no snapshot yet) every NPC is listed in the
 * given order at an unknown distance, so the panel never goes empty for want of a snapshot.
 */
export function nearbyNpcs<T extends NpcAt>(
  npcs: readonly T[],
  self: { x: number; z: number } | null,
  radiusM: number = NEARBY_NPC_RADIUS_M,
): NearbyNpcs<T> {
  if (!self) return { near: npcs.map((npc) => ({ npc, distanceM: NaN })), further: 0 };
  const measured = npcs.map((npc) => ({
    npc,
    distanceM: Math.hypot(npc.x - self.x, npc.z - self.z),
  }));
  const near = measured
    .filter((m) => m.distanceM <= radiusM)
    .sort((a, b) => a.distanceM - b.distanceM || a.npc.id.localeCompare(b.npc.id));
  return { near, further: measured.length - near.length };
}

/**
 * How close an NPC must be for the HUD's "[E] Talk" prompt (m). A little inside the
 * server's 6 m interact range (zone.ts `NPC_INTERACT_RANGE_M`), so the interpolated
 * position the client sees never offers a talk the server then silently drops.
 */
export const NPC_TALK_PROMPT_RANGE_M = 5.5;

/**
 * The NPC the HUD should offer to talk to: the nearest one within `rangeM`, or undefined
 * (also with no known own position — no prompt before the first snapshot).
 */
export function talkableNpc<T extends NpcAt>(
  npcs: readonly T[],
  self: { x: number; z: number } | null,
  rangeM: number = NPC_TALK_PROMPT_RANGE_M,
): { npc: T; distanceM: number } | undefined {
  if (!self) return undefined;
  return nearbyNpcs(npcs, self, rangeM).near[0];
}

/**
 * The "Nearby NPCs" list while a conversation is open (q-demo-dialogue-playtest-2026-10-29, owner
 * playtest): the NPC the player is already talking to is not offered again. Its "talk" re-opened
 * the same conversation (the greeting already heard, the answers shown so far cleared), which read
 * as a button that did nothing. Other NPCs nearby stay listed: talking to one starts a new
 * conversation. `talkingTo` null (no conversation) leaves the list unchanged.
 */
export function othersNearby<T extends NpcAt>(
  nearby: NearbyNpcs<T>,
  talkingTo: string | null,
): NearbyNpcs<T> {
  if (talkingTo === null) return nearby;
  return { near: nearby.near.filter((n) => n.npc.id !== talkingTo), further: nearby.further };
}
